/* app/providers/ToastProvider.jsx — стопка тостов bottom-right.
   API: useToast() → { show(message, tone, duration?) → id, dismiss(id) }.
   На mobile поднят над таб-баром (bottom: tab-bar+8) — баг #44.
   z-index: var(--z-toast).

   Доступность и время (kitchen-ux K-11, K-35, C-49; правила — components/ui/toastModel.js):
   · две ПОСТОЯННЫЕ live-области: polite и отдельная assertive для ошибок. Они в DOM и
     тогда, когда тостов нет, — скринридер надёжно объявляет только изменения уже
     существующей области;
   · авто-скрытие 4 с, ошибки — не меньше 8 с; наведение и фокус внутри стека ставят
     таймеры на паузу, уход — продолжает с остатка;
   · value контекста — useMemo: иначе каждый тост перерисовывал всех потребителей useToast;
   · стек не ложится на подвал открытого drawer/модалки (кнопки «Отмена/Сохранить»):
     поднимается над ним, если подвал под стеком (--toast-lift, CSS берёт max);
   · на узком экране (≤ 719.98 px) модальный диалог — лист снизу, и подъём над его подвалом
     ставил стек на текст диалога (сторож несохранённого, подтверждения; kitchen-ux E1). Стек
     встаёт над ВСЕМ листом, если помещается выше него; лист почти во весь экран — стек под
     диалогом по слою (.is-under-dialog), а таймеры стоят, пока диалог открыт: тост не
     пропадает невиданным (toastModel.dialogPlacement). Полноэкранные редакторы (drawer) —
     по-прежнему подвал: над ними места нет, а тосты редактора нужны. */

import React from 'react';
import ReactDOM from 'react-dom';
import { Toast } from '../../components/ui/Toast.jsx';
import { createToastTimers, dialogPlacement, footClearance, toastDuration, toastPoliteness } from '../../components/ui/toastModel.js';
import { getOverlayRoot } from '../../lib/overlay.jsx';

  const { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect, useLayoutEffect } = React;

  const ToastContext = createContext(null);
  let _id = 0;

  // подвалы оверлеев, над которыми стек обязан стоять
  const FOOT_SELECTOR = '.overlay .drawer__foot, .overlay .modal__foot, .overlay .entity-editor__foot';
  // модальные диалоги-листы узкого экрана: Modal, листы выбора (Select, фильтры, «Быстрая правка»,
  // пикеры, сортировка) и лист «Ещё» нижней панели (MobileTabBar; перепроверка Р3-1: 10-секундный
  // тост с кнопкой лежал на его пунктах «Пространство», нажатие уходило в тост)
  const DIALOG_SELECTOR = '.overlay--modal > .modal, .overlay--sheet > .select-sheet, .overlay > .sheet';
  // ширина, на которой Modal — лист снизу (components.css, мобильный лист)
  const NARROW_QUERY = '(max-width: 719.98px)';

  function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const [lift, setLift] = useState(0);
    const [under, setUnder] = useState(false);   // стек под диалогом по слою (E1)
    const timersRef = useRef(null);
    if (!timersRef.current) timersRef.current = createToastTimers();
    const hover = useRef({ pointer: false, focus: false, under: false });
    const stackRef = useRef(null);

    const remove = useCallback((id) => {
      setToasts((list) => list.filter((t) => t.id !== id));
    }, []);

    const dismiss = useCallback((id) => {
      timersRef.current.clear(id);
      remove(id);
    }, [remove]);

    const show = useCallback((message, tone = 'muted', duration) => {
      const id = ++_id;
      setToasts((list) => [...list, { id, message, tone }]);
      timersRef.current.start(id, toastDuration(tone, duration), () => remove(id));
      return id;
    }, [remove]);

    const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);

    // пауза: мышь ДВИГАЛАСЬ над стеком ИЛИ фокус внутри него ИЛИ стек под диалогом (E1);
    // продолжение — когда ушли все.
    // Именно pointermove, а не mouseenter/:hover: тост, всплывший под неподвижным курсором,
    // браузер помечает :hover и шлёт ему mouseenter без всякого движения — и такой тост
    // висел бы, пока человек не шевельнёт мышью. Реальное движение — сигнал «читаю».
    const syncPause = () => {
      const h = hover.current;
      if (h.pointer || h.focus || h.under) timersRef.current.pause();
      else timersRef.current.resume();
    };
    const onPointerMove = () => { if (!hover.current.pointer) { hover.current.pointer = true; syncPause(); } };
    const onPointerLeave = () => { hover.current.pointer = false; syncPause(); };
    const onFocus = () => { hover.current.focus = true; syncPause(); };
    const onBlur = (e) => {
      const stack = e && e.currentTarget;
      const next = e && e.relatedTarget;
      if (stack && next && stack.contains && stack.contains(next)) return; // фокус остался внутри
      hover.current.focus = false;
      syncPause();
    };

    useEffect(() => () => timersRef.current.dispose(), []);

    // Список изменился — пересверяем наведение и фокус по факту. Тост, закрытый прямо
    // под курсором, исчезает без pointerleave, и пауза иначе залипла бы навсегда.
    // Пересверка паузу только СНИМАЕТ: :hover без движения мыши её не ставит (см. выше).
    useLayoutEffect(() => {
      const stack = stackRef.current;
      if (!toasts.length || !stack || typeof stack.matches !== 'function') {
        hover.current.pointer = false;
        hover.current.focus = false;
      } else {
        hover.current.pointer = hover.current.pointer && stack.matches(':hover');
        hover.current.focus = typeof document !== 'undefined' && stack.contains(document.activeElement);
      }
      syncPause();
    }, [toasts.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Подъём над подвалом — только пока есть тосты: меряем, где подвалы открытых
    // оверлеев, и следим за открытием/закрытием drawer'ов (MutationObserver по корню
    // оверлеев — туда же порталятся Drawer/Modal).
    const hasToasts = toasts.length > 0;
    useLayoutEffect(() => {
      if (!hasToasts || typeof document === 'undefined' || typeof window === 'undefined') {
        hover.current.under = false;
        setLift(0); setUnder(false);
        return undefined;
      }
      const measure = () => {
        const stack = stackRef.current;
        const rects = (sel) => Array.from(document.querySelectorAll(sel)).map((el) => el.getBoundingClientRect());
        const r = stack ? stack.getBoundingClientRect() : null;
        const across = r ? { left: r.left, right: r.right } : null;
        const footLift = footClearance(rects(FOOT_SELECTOR), window.innerHeight, across);
        const narrow = typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches;
        const place = narrow
          ? dialogPlacement(rects(DIALOG_SELECTOR), window.innerHeight, r ? r.height : 0, across)
          : { lift: 0, under: false };
        // под диалогом таймеры стоят (тост ждёт, пока диалог закроют), вернулся — идут с остатка
        hover.current.under = place.under;
        syncPause();
        setLift(place.under ? footLift : Math.max(footLift, place.lift));
        setUnder(place.under);
      };
      measure();
      window.addEventListener('resize', measure);
      const root = getOverlayRoot();
      const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(measure) : null;
      if (mo) mo.observe(root, { childList: true, subtree: true });
      // Диалоги въезжают анимацией (modal-in, sheet-up): замер в момент вставки видит лист ниже,
      // чем он встанет, — по концу анимации меряем снова (E1: иначе стек заходил на лист).
      const listens = !!root && typeof root.addEventListener === 'function';
      if (listens) root.addEventListener('animationend', measure);
      return () => {
        window.removeEventListener('resize', measure);
        if (mo) mo.disconnect();
        if (listens) root.removeEventListener('animationend', measure);
      };
    }, [hasToasts]);

    const byMode = (mode) => toasts
      .filter((t) => toastPoliteness(t.tone) === mode)
      .map((t) => <Toast key={t.id} message={t.message} tone={t.tone} onDismiss={() => dismiss(t.id)} />);

    return (
      <ToastContext.Provider value={value}>
        {children}
        {ReactDOM.createPortal(
          <div className={'toast-stack' + (under ? ' is-under-dialog' : '')} role="region" aria-label="Уведомления" ref={stackRef}
            style={lift ? { '--toast-lift': lift + 'px' } : undefined}
            onPointerMove={onPointerMove} onPointerLeave={onPointerLeave} onFocus={onFocus} onBlur={onBlur}>
            <div className="toast-stack__list" aria-live="assertive" aria-atomic="false">{byMode('assertive')}</div>
            <div className="toast-stack__list" aria-live="polite" aria-atomic="false">{byMode('polite')}</div>
          </div>,
          getOverlayRoot()
        )}
      </ToastContext.Provider>
    );
  }

  const useToast = () => useContext(ToastContext);

export { ToastProvider };
export { useToast };
